// cybiko.io main entry. Boots overlay, wires hitboxes, posts input to worker.
//
// Phase A: scaffold
// Phase E+H: device frame, LCD canvas, asset pipeline
// Phase F+G: hitboxes from KEYS + visual press-state overlays
// Phase D: worker thread + stub emulator + frame rendering + Esc power toggle
// Phase E continuation: keyboard binding + hitbox→worker key dispatch + audio
// Phase I: RF / BroadcastChannel chat

import { initCalibration } from './calibration';
import { applyStoredLcdBorderBounds, initLcdBorderCalibration } from './lcd-border-calibration';
import {
  LCD_ACTIVE_SCALE,
  loadOverlayMeta,
  applyBounds,
  applyLcdBounds,
  boundsFromInnerAlignment,
  scaleBoundsFromCenter,
  type LcdBounds,
  type OverlayMeta,
} from './overlay';
import { initLayout, setKeyOverlay } from './layout';
import { initRf } from './rf';
import { initInfoModal } from './info';
import { initByorControls } from './byor';
import { KEYBOARD_BINDINGS } from './keymap';
import type { CyLmHostShell } from './cylm';
import {
  createNativeCylmBridge,
  shouldInitNativeCylmBridge,
  type NativeCylmBridge,
} from './cylm-native-bridge';
import {
  createNativeRfChatBridge,
  createOnlineNativeRfChatTransport,
  createUnavailableNativeRfChatTransport,
  nativeRfChatOnlineRoomId,
  nativeRfChatRuntimeMode,
  shouldInitNativeRfChatBridge,
  type NativeRfChatBridge,
  type NativeRfChatTransport,
} from './native-rf-chat-bridge';
import { initConsoleSideload, recordConsoleSideloadTxByte } from './sideload';
import { initRfCapture } from './rf-capture';
import {
  LCD_WIDTH,
  LCD_HEIGHT,
  type MainToWorker,
  type WorkerToMain,
} from './protocol';

const frame = document.querySelector<HTMLElement>('.device-frame');
const lcdWindow = document.querySelector<HTMLElement>('.lcd-window');
const lcdCanvas = document.getElementById('lcd') as HTMLCanvasElement | null;
const lcdEffect = document.querySelector<HTMLElement>('.lcd-effect');
const lcdWindowOverlay = document.querySelector<HTMLElement>('.lcd-window-overlay');
const pressLayer = document.querySelector<HTMLElement>('.device-press-layer');

if (!frame || !lcdWindow || !lcdCanvas || !lcdEffect || !lcdWindowOverlay || !pressLayer) {
  throw new Error('Required DOM nodes missing — check index.html');
}

const lcdCtx = lcdCanvas.getContext('2d', { alpha: false });
if (!lcdCtx) throw new Error('2D context unsupported');

const cylmHostMode = new URLSearchParams(window.location.search).get('cylm') === 'host';
const onlineRfMode = new URLSearchParams(window.location.search).get('onlineRf');
const localStorageResetRequested = shouldResetLocalSiteStorage();
const rfCapture = initRfCapture();
let cylmHostShell: CyLmHostShell | undefined;
let nativeCylmBridge: NativeCylmBridge | undefined;
let nativeRfChatBridge: NativeRfChatBridge | undefined;
let nativeConsoleRxQueue: Promise<void> = Promise.resolve();
const pendingFlashDumps = new Map<
  string,
  {
    readonly resolve: (buffer: ArrayBuffer) => void;
    readonly reject: (reason?: unknown) => void;
    readonly timeout: number;
  }
>();

const blankFrame = lcdCtx.createImageData(LCD_WIDTH, LCD_HEIGHT);
for (let i = 0; i < blankFrame.data.length; i += 4) {
  blankFrame.data[i] = 184;
  blankFrame.data[i + 1] = 196;
  blankFrame.data[i + 2] = 160;
  blankFrame.data[i + 3] = 255;
}
lcdCtx.putImageData(blankFrame, 0, 0);

let worker: Worker | undefined;
if (!cylmHostMode && !localStorageResetRequested) {
  const workerUrl = new URL('./worker.ts', import.meta.url);
  workerUrl.search = window.location.search;
  worker = new Worker(workerUrl, {
    type: 'module',
    name: 'cybiko-emulator',
  });
}

let booted = false;
let deviceState: 'off' | 'booting' | 'running' = 'off';
let reuseFrame: ImageData | undefined;
let audioCtx: AudioContext | undefined;
// Track which Cybiko keys are currently held — for OS-level repeat handling
// and proper release on blur.
const heldKeys = new Set<string>();
const ESC_POWER_HOLD_MS = 1200;
const nativeConsolePaceMs = readNativeConsolePaceMs();
let escHoldTimer: number | undefined;
let escPowerOnGesture = false;
let escPowerOffGesture = false;

initLcdMode();

if (worker) {
  worker.addEventListener('message', (e: MessageEvent<WorkerToMain>) => {
    rfCapture?.recordWorkerMessage(e.data);
    handleWorkerMessage(e.data);
  });

  worker.addEventListener('error', (e) => {
    console.error('Worker error:', e.message, e.filename, e.lineno);
  });
}

if (localStorageResetRequested) {
  void resetLocalSiteStorageAndReload();
} else {
  bootstrap(frame, pressLayer).catch((err) => {
    console.error('Bootstrap failed:', err);
  });
}

async function bootstrap(frameEl: HTMLElement, presses: HTMLElement): Promise<void> {
  const meta = await loadOverlayMeta();
  const activeLcdScale = readRootNumberVar('--lcd-active-scale', LCD_ACTIVE_SCALE);
  const activeLcdBounds = scaleBoundsFromCenter(meta.lcd_active_bounds, activeLcdScale);
  applyBounds(lcdWindow!, meta.lcd_window_bounds);
  applyLcdBounds(lcdCanvas!, activeLcdBounds);
  applyBounds(lcdEffect!, activeLcdBounds);
  const lcdBorderBounds = configureLcdWindowOverlay(lcdWindowOverlay!, meta, meta.lcd_active_bounds);
  if (cylmHostMode) {
    const { createCyLmHostShell } = await import('./cylm');
    cylmHostShell = createCyLmHostShell(lcdCanvas!);
  }
  buildPressOverlays(presses, meta);

  initLayout();
  initCalibration(frameEl);
  if (lcdBorderBounds) initLcdBorderCalibration(frameEl, lcdWindowOverlay!);
  initInfoModal();
  initByorControls({
    exportRunningFlash: worker ? requestFlashDump : undefined,
    reloadPage: () => window.location.reload(),
  });
  const privateOnlineModule = shouldLoadPrivateOnlineState()
    ? await import('./private-online-state')
    : undefined;
  const privateOnlineState = privateOnlineModule
    ? await privateOnlineModule.initPrivateOnlineState()
    : undefined;
  wireHitboxes();
  wirePhysicalKeyboard();
  wireTouchKeyboard();
  wireBlur();
  if (worker) {
    if (onlineRfMode === 'mock') {
      const { initMockOnlineRfBridge } = await import('./online-rf');
      const privateRoomIdentity = privateOnlineModule && privateOnlineState
        ? privateOnlineModule.privateOnlineRoomIdentity(privateOnlineState.getState(), window.location.search)
        : undefined;
      initMockOnlineRfBridge(worker, { capture: rfCapture, ...privateRoomIdentity });
    } else if (onlineRfMode === 'backend') {
      const { initBackendOnlineRfBridge } = await import('./online-rf');
      try {
        await initBackendOnlineRfBridge(worker, { capture: rfCapture });
      } catch (err) {
        console.warn(
          '[online-rf] backend mode unavailable; falling back to same-browser BroadcastChannel',
          err,
        );
        initRf(worker, { capture: rfCapture });
      }
    } else {
      initRf(worker, { capture: rfCapture });
    }
  }
  initConsoleSideload(worker);
  initNativeCylmBridge(worker);
  await initNativeRfChatBridge(worker);

  frameEl.dataset.state = 'off';
}

function shouldLoadPrivateOnlineState(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('onlineState') || params.get('onlineRf') === 'backend';
}

function shouldResetLocalSiteStorage(): boolean {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('resetStorage') === '1' &&
    import.meta.env.DEV &&
    isLocalDevHost(window.location.hostname)
  );
}

function isLocalDevHost(hostname: string): boolean {
  return hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]';
}

async function resetLocalSiteStorageAndReload(): Promise<void> {
  console.info('[dev-reset] clearing localhost site storage for Cybiko IDBFS');
  try {
    localStorage.clear();
    sessionStorage.clear();
    await clearLocalCaches();
    await clearIndexedDatabases();
  } catch (err) {
    console.warn('[dev-reset] site storage reset completed with a warning', err);
  }

  const next = new URL(window.location.href);
  next.searchParams.delete('resetStorage');
  window.location.replace(next.toString());
}

async function clearLocalCaches(): Promise<void> {
  if (!('caches' in window)) return;
  const names = await caches.keys();
  await Promise.all(names.map((name) => caches.delete(name)));
}

async function clearIndexedDatabases(): Promise<void> {
  if (typeof indexedDB.databases === 'function') {
    const databases = await indexedDB.databases();
    const names = databases
      .map((database) => database.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0);
    await Promise.all(names.map((name) => deleteIndexedDatabase(name)));
    return;
  }

  await Promise.all([
    'EM_FS_cybiko',
    'EM_FS_/cybiko',
    '/cybiko',
    'cybiko',
  ].map((name) => deleteIndexedDatabase(name)));
}

function deleteIndexedDatabase(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onblocked = () => resolve();
    request.onerror = () => reject(request.error ?? new Error(`failed to delete IndexedDB ${name}`));
  });
}

function readRootNumberVar(name: string, fallback: number): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function initLcdMode(): void {
  const params = new URLSearchParams(window.location.search);
  document.body.classList.toggle('lcd-raw', params.get('lcd') === 'raw');
  document.body.classList.toggle('cylm-host-mode', cylmHostMode);
  const backdrop = params.get('bg');
  const knownBackdrops = new Set(['solid-mauve', 'blue', 'violet', 'gold']);
  if (backdrop && knownBackdrops.has(backdrop)) {
    document.body.dataset.backdrop = backdrop;
  } else {
    document.body.dataset.backdrop ??= 'solid-mauve';
  }
  const layout = params.get('layout');
  if (layout === 'matte') {
    document.body.dataset.layout = layout;
  } else {
    document.body.dataset.layout ??= 'matte';
  }
}

function handleWorkerMessage(msg: WorkerToMain): void {
  switch (msg.type) {
    case 'ready':
      break;
    case 'state':
      deviceState = msg.state;
      if (frame) frame.dataset.state = msg.state === 'off' ? 'off' : 'on';
      // Reset booted gate if device powers itself off
      if (msg.state === 'off') {
        booted = false;
        if (lcdCtx) lcdCtx.putImageData(blankFrame, 0, 0);
      }
      break;
    case 'frame':
      drawFrame(msg.buffer, msg.width, msg.height);
      break;
    case 'rf-tx':
      // Phase I forwards to BroadcastChannel
      break;
    case 'console-tx':
      recordConsoleSideloadTxByte(msg.byte);
      nativeCylmBridge?.receiveConsoleByte(msg.byte);
      nativeRfChatBridge?.receiveConsoleByte(msg.byte);
      break;
    case 'flash-dump':
      completeFlashDump(msg.requestId, msg.buffer);
      break;
    case 'flash-dump-error':
      failFlashDump(msg.requestId, msg.message);
      break;
    case 'log':
      console.log('[worker]', msg.message);
      break;
    default: {
      const _exhaustive: never = msg;
      void _exhaustive;
    }
  }
}

function drawFrame(buffer: ArrayBuffer, w: number, h: number): void {
  if (!lcdCtx) return;
  if (w !== LCD_WIDTH || h !== LCD_HEIGHT) return;
  if (!reuseFrame || reuseFrame.width !== w) {
    reuseFrame = lcdCtx.createImageData(w, h);
  }
  reuseFrame.data.set(new Uint8ClampedArray(buffer));
  lcdCtx.putImageData(reuseFrame, 0, 0);
}

function send(msg: MainToWorker): void {
  if (!worker) return;
  rfCapture?.recordMainMessage(msg, 'worker');
  worker.postMessage(msg);
}

function requestFlashDump(): Promise<ArrayBuffer> {
  if (!worker) return Promise.reject(new Error('emulator worker is not available'));
  const requestId = crypto.randomUUID();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      pendingFlashDumps.delete(requestId);
      reject(new Error('timed out waiting for flash dump'));
    }, 10_000);
    pendingFlashDumps.set(requestId, { resolve, reject, timeout });
    send({ type: 'dump-flash', requestId });
  });
}

function completeFlashDump(requestId: string, buffer: ArrayBuffer): void {
  const pending = pendingFlashDumps.get(requestId);
  if (!pending) return;
  window.clearTimeout(pending.timeout);
  pendingFlashDumps.delete(requestId);
  pending.resolve(buffer);
}

function failFlashDump(requestId: string, message: string): void {
  const pending = pendingFlashDumps.get(requestId);
  if (!pending) return;
  window.clearTimeout(pending.timeout);
  pendingFlashDumps.delete(requestId);
  pending.reject(new Error(message));
}

function initNativeCylmBridge(activeWorker: Worker | undefined): void {
  if (!activeWorker) return;
  if (!shouldInitNativeCylmBridge(window.location.search)) return;
  nativeCylmBridge = createNativeCylmBridge({
    sendConsoleText: queueNativeConsoleText,
    logWarning: (message) => console.warn('[cylm-native]', message),
  });
  window.__cylmNativeBridgeDebug = () => nativeCylmBridge?.debugState();
  window.__cylmNativeBridgeReceiveConsoleText = async (text: string) => {
    nativeCylmBridge?.receiveConsoleText(text);
    await nativeCylmBridge?.settled();
  };
  console.info('[cylm-native] native app-host bridge armed; waiting for CYLM1 console lines');
}

async function initNativeRfChatBridge(activeWorker: Worker | undefined): Promise<void> {
  if (!activeWorker) return;
  if (!shouldInitNativeRfChatBridge(window.location.search)) return;
  const params = new URLSearchParams(window.location.search);
  const displayName = params.get('rfName') ?? undefined;
  const mode = nativeRfChatRuntimeMode(window.location.search);
  let transport: NativeRfChatTransport | undefined;
  if (mode === 'online') {
    try {
      const { BackendOnlineRoomClient, backendInviteCode } = await import('./online-backend');
      const client = new BackendOnlineRoomClient({
        roomId: nativeRfChatOnlineRoomId(window.location.search),
        inviteCode: backendInviteCode(window.location.search),
      });
      await client.connect();
      transport = createOnlineNativeRfChatTransport(client, {
        logWarning: (message) => console.warn('[rfchat-native]', message),
      });
    } catch (err) {
      console.warn('[rfchat-native] online private room unavailable; native RF chat bridge armed in offline mode', err);
      transport = createUnavailableNativeRfChatTransport('CONNECT FAILED');
    }
  }
  nativeRfChatBridge = createNativeRfChatBridge({
    displayName,
    transport,
    sendConsoleText: queueNativeConsoleText,
    logWarning: (message) => console.warn('[rfchat-native]', message),
  });
  window.__nativeRfChatBridgeDebug = () => nativeRfChatBridge?.debugState();
  window.__nativeRfChatBridgeReceiveConsoleText = (text: string) => {
    nativeRfChatBridge?.receiveConsoleText(text);
  };
  console.info('[rfchat-native] native RF chat bridge armed; waiting for CYRF1 console lines');
}

function queueNativeConsoleText(text: string): void {
  nativeConsoleRxQueue = nativeConsoleRxQueue
    .catch((err: unknown) => {
      console.warn('[native-console]', err instanceof Error ? err.message : String(err));
    })
    .then(() => sendNativeConsoleTextPaced(text));
}

async function sendNativeConsoleTextPaced(text: string): Promise<void> {
  for (let index = 0; index < text.length; index += 1) {
    send({ type: 'console-rx', text: text[index] ?? '' });
    await delay(nativeConsolePaceMs);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function readNativeConsolePaceMs(): number {
  const raw = new URLSearchParams(window.location.search).get('nativeConsolePaceMs');
  const parsed = raw ? Number(raw) : 500;
  if (!Number.isFinite(parsed)) return 500;
  return Math.max(1, Math.min(500, Math.round(parsed)));
}

// ---- Power-on gating ----

/**
 * The user's first interaction (click anywhere within the page) is the
 * gesture that unlocks the AudioContext. We resume it eagerly so audio
 * will be usable when the real MAME-WASM lands without needing a second
 * click.
 */
async function unlockAudio(): Promise<void> {
  if (audioCtx) return;
  try {
    audioCtx = new AudioContext();
    if (audioCtx.state === 'suspended') {
      await audioCtx.resume();
    }
  } catch (err) {
    console.warn('AudioContext unavailable:', err);
  }
}

async function triggerPowerOn(): Promise<void> {
  await unlockAudio();
  booted = true;
  if (cylmHostShell) {
    deviceState = 'running';
    if (frame) frame.dataset.state = 'on';
    cylmHostShell.start();
    return;
  }
  send({ type: 'power-on' });
  // Brief CRT-wake flash
  if (lcdCtx) {
    lcdCtx.fillStyle = '#2a2a28';
    lcdCtx.fillRect(0, 0, LCD_WIDTH, LCD_HEIGHT);
  }
}

function triggerPowerOff(): void {
  if (cylmHostShell) {
    deviceState = 'off';
    booted = false;
    if (frame) frame.dataset.state = 'off';
    cylmHostShell.stop();
    return;
  }
  send({ type: 'power-off' });
  setTimeout(() => {
    if (lcdCtx && deviceState === 'off') lcdCtx.putImageData(blankFrame, 0, 0);
  }, 100);
}

// ---- Hitbox wiring ----

function wireHitboxes(): void {
  const hitboxes = document.querySelectorAll<HTMLElement>('.hitbox');
  for (const hit of hitboxes) {
    const key = hit.dataset.key;
    if (!key) continue;
    if (key === 'esc') {
      wireEscHitbox(hit);
      continue;
    }
    wireKeyHitbox(hit, key);
  }
}

function wireEscHitbox(el: HTMLElement): void {
  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    beginEscGesture();
  };
  const onUp = (): void => {
    finishEscGesture();
    el.blur();
  };
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
  el.addEventListener('pointerleave', (e) => {
    if (el.hasPointerCapture(e.pointerId) === false) return;
    onUp();
  });
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
  });
}

function wireKeyHitbox(el: HTMLElement, keyId: string): void {
  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    pressKey(keyId);
  };
  const onUp = (): void => {
    releaseKey(keyId);
    el.blur();
  };
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
  el.addEventListener('pointerleave', (e) => {
    if (el.hasPointerCapture(e.pointerId) === false) return;
    onUp();
  });
  // Suppress the synthesized click+keypress: when a hitbox has focus and the
  // user presses Enter/Space, the browser fires click on the button. We rely
  // on the window-level keydown handler instead, so explicitly prevent this.
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
    }
  });
}

function pressKey(keyId: string): void {
  if (heldKeys.has(keyId)) return; // already held
  heldKeys.add(keyId);
  setKeyOverlay(keyId, true);
  if (deviceState === 'running' || deviceState === 'booting') {
    if (cylmHostShell) {
      cylmHostShell.keydown(keyId);
      return;
    }
    send({ type: 'keydown', key: keyId });
  }
}

function releaseKey(keyId: string): void {
  if (!heldKeys.has(keyId)) return;
  heldKeys.delete(keyId);
  setKeyOverlay(keyId, false);
  if (deviceState === 'running' || deviceState === 'booting') {
    if (cylmHostShell) {
      cylmHostShell.keyup(keyId);
      return;
    }
    send({ type: 'keyup', key: keyId });
  }
}

function beginEscGesture(): void {
  if (escHoldTimer !== undefined || escPowerOnGesture) return;
  setKeyOverlay('esc', true);

  if (!booted || deviceState === 'off') {
    escPowerOnGesture = true;
    void triggerPowerOn();
    return;
  }

  escPowerOffGesture = false;
  escHoldTimer = window.setTimeout(() => {
    escHoldTimer = undefined;
    escPowerOffGesture = true;
    setKeyOverlay('esc', false);
    triggerPowerOff();
  }, ESC_POWER_HOLD_MS);
}

function finishEscGesture(): void {
  if (escPowerOnGesture) {
    escPowerOnGesture = false;
    setKeyOverlay('esc', false);
    return;
  }

  if (escHoldTimer !== undefined) {
    window.clearTimeout(escHoldTimer);
    escHoldTimer = undefined;
    setKeyOverlay('esc', false);
    if (deviceState === 'running' || deviceState === 'booting') {
      tapKey('esc');
    }
    return;
  }

  if (escPowerOffGesture) {
    escPowerOffGesture = false;
    setKeyOverlay('esc', false);
  }
}

function tapKey(keyId: string): void {
  pressKey(keyId);
  window.setTimeout(() => releaseKey(keyId), 60);
}

// ---- Physical keyboard ----

function lookupKeyId(ev: KeyboardEvent): string | undefined {
  return KEYBOARD_BINDINGS.get(ev.key);
}

function shouldSuppressBrowserDefault(key: string): boolean {
  if (/^F[1-9]\d?$/.test(key)) return true;
  if (key.startsWith('Arrow')) return true;
  if (key === 'Tab') return true;
  if (key === 'Escape') return true;
  return false;
}

function wirePhysicalKeyboard(): void {
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === 'Escape') {
      const modal = document.getElementById('info-modal');
      if (modal && !modal.hidden) return; // modal handles its own escape
    }

    const keyId = lookupKeyId(e);
    if (!keyId) return;

    if (shouldSuppressBrowserDefault(e.key)) e.preventDefault();

    if (keyId === 'esc') {
      if (e.repeat) return;
      beginEscGesture();
      return;
    }

    if (e.repeat) return; // emulator handles its own repeat
    pressKey(keyId);
  });

  window.addEventListener('keyup', (e) => {
    const keyId = lookupKeyId(e);
    if (!keyId) return;
    if (shouldSuppressBrowserDefault(e.key)) e.preventDefault();
    if (keyId === 'esc') {
      finishEscGesture();
      return;
    }
    releaseKey(keyId);
  });
}

// ---- On-screen retro keyboard (touch devices) ----
// Touch devices have no physical keyboard, and the iOS soft keyboard garbles
// fast input via autocorrect. Render a Cybiko-style keyboard docked below the
// device for the keys NOT on the device overlay: letters, digits, space, enter,
// backspace and the four arrows. Each key drives the same pressKey/releaseKey
// path as the device hitboxes, so routing matches the hardware and holding a
// key auto-repeats. Esc / Del / Ins / ? / function buttons / d-pad stay on the
// overlay (already tap targets) and are intentionally not duplicated here.
interface TouchKeySpec {
  readonly id: string;
  readonly label: string;
  readonly cls?: string;
}

function wireTouchKeyboard(): void {
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const touch = (navigator.maxTouchPoints || 0) > 0;
  if (!coarse && !touch) return;

  const letters = (s: string): TouchKeySpec[] =>
    s.split('').map((c) => ({ id: c, label: c }));
  const rows: TouchKeySpec[][] = [
    letters('1234567890'),
    letters('qwertyuiop'),
    letters('asdfghjkl'),
    [...letters('zxcvbnm'), { id: 'backspace', label: '⌫', cls: 'wide' }],
    [
      { id: 'left', label: '◀', cls: 'arrow' },
      { id: 'up', label: '▲', cls: 'arrow' },
      { id: 'down', label: '▼', cls: 'arrow' },
      { id: 'right', label: '▶', cls: 'arrow' },
      { id: 'space', label: 'space', cls: 'space' },
      { id: 'enter', label: '⏎', cls: 'wide' },
    ],
  ];

  const kb = document.createElement('div');
  kb.className = 'touch-keyboard';
  kb.setAttribute('role', 'group');
  kb.setAttribute('aria-label', 'On-screen keyboard');

  for (const row of rows) {
    const rowEl = document.createElement('div');
    rowEl.className = 'tk-row';
    for (const spec of row) {
      const key = document.createElement('button');
      key.type = 'button';
      key.className = spec.cls ? `tk-key tk-key--${spec.cls}` : 'tk-key';
      key.textContent = spec.label;
      key.setAttribute('aria-label', spec.id);
      wireTouchKey(key, spec.id);
      rowEl.appendChild(key);
    }
    kb.appendChild(rowEl);
  }

  const credits = document.querySelector('.credits');
  if (credits) document.body.insertBefore(kb, credits);
  else document.body.appendChild(kb);
  document.body.classList.add('has-touch-keyboard');
}

function wireTouchKey(el: HTMLElement, keyId: string): void {
  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    pressKey(keyId);
  };
  const onUp = (): void => {
    releaseKey(keyId);
    el.blur();
  };
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
  el.addEventListener('pointerleave', (e) => {
    if (!el.hasPointerCapture(e.pointerId)) return;
    onUp();
  });
}

/** Release any held keys when the window loses focus (drag off, alt-tab, etc.). */
function wireBlur(): void {
  window.addEventListener('blur', () => {
    if (escHoldTimer !== undefined) {
      window.clearTimeout(escHoldTimer);
      escHoldTimer = undefined;
    }
    escPowerOnGesture = false;
    escPowerOffGesture = false;
    setKeyOverlay('esc', false);
    for (const k of [...heldKeys]) {
      releaseKey(k);
    }
  });
}

// ---- Press-state overlay layer ----

function buildPressOverlays(layer: HTMLElement, meta: OverlayMeta): void {
  const baseName = meta.base.replace(/\.webp$/, '');
  const lcdWindowOverlayName = meta.lcd_window_overlay?.replace(/\.webp$/, '');
  for (const file of meta.files) {
    if (file.name === baseName) continue;
    if (lcdWindowOverlayName && file.name === lcdWindowOverlayName) continue;
    const img = document.createElement('img');
    img.className = 'device-press';
    img.src = `/assets/${file.name}.webp`;
    img.alt = '';
    img.dataset.overlay = file.name;
    img.draggable = false;
    img.decoding = 'async';
    img.loading = 'eager';
    layer.appendChild(img);
  }
}

function configureLcdWindowOverlay(el: HTMLElement, meta: OverlayMeta, activeLcdBounds: LcdBounds): LcdBounds | undefined {
  if (!meta.lcd_window_overlay) {
    applyBounds(el, meta.lcd_window_bounds);
    el.style.backgroundImage = '';
    el.removeAttribute('data-asset');
    delete el.dataset.holeLeft;
    delete el.dataset.holeTop;
    delete el.dataset.holeWidth;
    delete el.dataset.holeHeight;
    return undefined;
  }

  const overlayBounds = meta.lcd_window_overlay_bounds ?? (meta.lcd_window_overlay_hole_bounds
    ? boundsFromInnerAlignment(activeLcdBounds, meta.lcd_window_overlay_hole_bounds)
    : meta.lcd_window_bounds);
  const appliedBounds = applyStoredLcdBorderBounds(el, overlayBounds);
  el.style.backgroundImage = `url("/assets/${meta.lcd_window_overlay}")`;
  el.dataset.asset = meta.lcd_window_overlay;
  if (meta.lcd_window_overlay_hole_bounds) {
    el.dataset.holeLeft = String(meta.lcd_window_overlay_hole_bounds.left);
    el.dataset.holeTop = String(meta.lcd_window_overlay_hole_bounds.top);
    el.dataset.holeWidth = String(meta.lcd_window_overlay_hole_bounds.width);
    el.dataset.holeHeight = String(meta.lcd_window_overlay_hole_bounds.height);
  } else {
    delete el.dataset.holeLeft;
    delete el.dataset.holeTop;
    delete el.dataset.holeWidth;
    delete el.dataset.holeHeight;
  }
  return appliedBounds;
}
